import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, CheckCircle2, Banknote, Gift, Ban, Pencil, Trash2, Gavel, Undo2 } from 'lucide-react';
import { SeverityBadge, StatusBadge, SourceBadge } from './PenaltyBadges';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function deductionLabel(d) {
  if (!d) return '';
  const kind = d.type === 'incentive' ? 'Incentive' : 'Salary';
  const m = MONTHS_SHORT[(d.month || 1) - 1] || d.month;
  return `${kind} · ${m} ${d.year}`;
}

export default function PenaltyTable({ penalties, canDeduct, canRevert, onAcknowledge, onDeduct, onWaive, onEdit, onDelete, onRevert }) {
  if (penalties.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="penalties-empty">
        <Gavel className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="font-medium">No penalties found</p>
        <p className="text-sm mt-1">Penalties from your Zoho Form or manual entries will appear here.</p>
      </div>
    );
  }

  return (
    <Table data-testid="penalties-table">
      <TableHeader>
        <TableRow>
          <TableHead>Employee</TableHead>
          <TableHead>Organization</TableHead>
          <TableHead>Incident</TableHead>
          <TableHead>Shortcoming</TableHead>
          <TableHead>Severity</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="text-center">Occ.</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-10"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {penalties.map((p) => (
          <TableRow key={p.id} data-testid={`penalty-row-${p.id}`}>
            <TableCell>
              <div className="font-medium text-sm">{p.employee_name || '—'}</div>
              <div className="text-xs text-muted-foreground font-mono">{p.employee_code || 'unlinked'}</div>
            </TableCell>
            <TableCell className="text-sm">{p.organization_name || '—'}</TableCell>
            <TableCell className="text-sm whitespace-nowrap">{p.incident_date || '—'}</TableCell>
            <TableCell>
              <div className="text-sm">{p.category || '—'}{p.subcategory ? <span className="text-muted-foreground"> / {p.subcategory}</span> : null}</div>
              {p.action && <div className="text-xs text-indigo-600 dark:text-indigo-400">Action: {p.action}</div>}
              {p.description && <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={p.description}>{p.description}</div>}
              {p.internal_notes && <div className="text-xs italic text-muted-foreground truncate max-w-[200px]" title={p.internal_notes}>Note: {p.internal_notes}</div>}
            </TableCell>
            <TableCell><SeverityBadge severity={p.severity} /></TableCell>
            <TableCell className="text-right font-mono text-sm">{p.amount ? fmt(p.amount) : '—'}</TableCell>
            <TableCell className="text-center">
              {p.occurrence_count > 1 ? (
                <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">{p.occurrence_count}×</Badge>
              ) : (
                <span className="text-xs text-muted-foreground">1×</span>
              )}
            </TableCell>
            <TableCell><SourceBadge source={p.source} /></TableCell>
            <TableCell>
              <StatusBadge status={p.status} />
              {p.status === 'deducted' && p.deduction && (
                <div className="text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap" data-testid={`deduction-label-${p.id}`}>{deductionLabel(p.deduction)}</div>
              )}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`penalty-actions-${p.id}`}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {p.status === 'pending' && (
                    <DropdownMenuItem onClick={() => onAcknowledge(p)} data-testid="penalty-ack-action">
                      <CheckCircle2 className="h-4 w-4 mr-2" />Acknowledge
                    </DropdownMenuItem>
                  )}
                  {canDeduct && p.status !== 'deducted' && p.status !== 'waived' && p.amount > 0 && (
                    <DropdownMenuItem onClick={() => onDeduct(p, 'salary')} data-testid="penalty-deduct-action">
                      <Banknote className="h-4 w-4 mr-2" />Deduct from Salary
                    </DropdownMenuItem>
                  )}
                  {canRevert && p.status !== 'deducted' && p.status !== 'waived' && p.amount > 0 && (
                    <DropdownMenuItem onClick={() => onDeduct(p, 'incentive')} data-testid="penalty-deduct-incentive-action">
                      <Gift className="h-4 w-4 mr-2" />Deduct from Incentive
                    </DropdownMenuItem>
                  )}
                  {p.status !== 'deducted' && p.status !== 'waived' && (
                    <DropdownMenuItem onClick={() => onWaive(p)} data-testid="penalty-waive-action">
                      <Ban className="h-4 w-4 mr-2" />Waive
                    </DropdownMenuItem>
                  )}
                  {canRevert && p.status === 'deducted' && (
                    <DropdownMenuItem onClick={() => onRevert(p)} data-testid="penalty-revert-action">
                      <Undo2 className="h-4 w-4 mr-2" />Revert Deduction
                    </DropdownMenuItem>
                  )}
                  {p.status !== 'deducted' && (
                    <>
                      <DropdownMenuItem onClick={() => onEdit(p)} data-testid="penalty-edit-action">
                        <Pencil className="h-4 w-4 mr-2" />Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => onDelete(p)} data-testid="penalty-delete-action">
                        <Trash2 className="h-4 w-4 mr-2" />Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
